<script lang="ts" setup>
import type { PrimitiveFieldPath } from '#layers/data-model/utils/collection-field-path';
import type { FilterSchemaCollection } from './utils/filter-schema';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { injectFilterSchemaContext } from './utils/filter-schema-context';

const props = withDefaults(defineProps<{
	collection: FilterSchemaCollection;
	includeRelationFields?: boolean;
	disabled?: boolean;
}>(), {
	includeRelationFields: true,
});

const emit = defineEmits<{
	addGroup: [];
	addFilter: [fieldPath: PrimitiveFieldPath];
}>();

const { t } = useI18n();
const { findOppositeCollectionByRelationFieldId } = injectFilterSchemaContext();
const includeSelectableRelationFields = computed(() => props.includeRelationFields !== false);

const ADD_GROUP_ACTION_ID = 'add-group';

const leadingActions = computed(() => [{
	id: ADD_GROUP_ACTION_ID,
	label: t('filters.filter_group'),
	icon: 'lucide:plus',
}]);

const availableFilters = computed(() => [
	...props.collection.primitiveFields,
	...(props.collection.relationFields ?? []).filter((field) => findOppositeCollectionByRelationFieldId(field.id)),
]);

function onFieldSelect(fieldPath: PrimitiveFieldPath) {
	emit('addFilter', fieldPath);
}

function onAction(actionId: string) {
	if (actionId === ADD_GROUP_ACTION_ID) {
		emit('addGroup');
	}
}
</script>

<template>
	<CollectionFieldPathSelect
		:collection
		include-constrained-fields
		:include-relation-fields="includeSelectableRelationFields"
		:include-list-relation-fields="includeSelectableRelationFields"
		order="schema"
		:resolve-relation-collection="findOppositeCollectionByRelationFieldId"
		:leading-actions
		:search-placeholder="$t('filters.filter_search_placeholder')"
		@action="onAction"
		@select="onFieldSelect"
	>
		<template #default="{ open }">
			<slot
				:available-filters
				:open
			>
				<DButton
					color="neutral"
					variant="ghost"
					icon="lucide:list-filter"
					:aria-label="$t('filters.filter')"
					:label="$t('filters.add_filter')"
					size="sm"
					:disabled
				/>
			</slot>
		</template>
	</CollectionFieldPathSelect>
</template>
